/**
 * Google Docs style autosave for the draft editor.
 *
 * An edit schedules a save once typing pauses; continuous typing still saves
 * every `AUTOSAVE_MAX_MS`. One save runs at a time: edits that land while it
 * is in flight schedule the next one when it settles. A failed save retries
 * with backoff (edits do not cut the wait short, so an outage is not hammered),
 * and a fatal one (a revision conflict) stops until the host resumes autosave.
 * The server folds these saves into one version per editing session.
 */
import { useEffect, useMemo, useRef } from "react";

export const AUTOSAVE_IDLE_MS = 2000;
export const AUTOSAVE_MAX_MS = 15000;
export const AUTOSAVE_RETRY_MS = [5000, 15000, 30000, 60000];

type AutosaveOptions = {
  isDirty: () => boolean;
  save: () => Promise<void>;
  /** A failure retrying cannot fix; autosave waits for the host to resume it. */
  isFatal?: (error: unknown) => boolean;
};

export type Autosave = {
  /** The document changed. */
  edited: () => void;
  /** Save now if there is anything to save (the tab is being hidden). */
  flush: () => void;
  /** Pause (false) or resume (true); resuming saves pending edits. */
  setActive: (active: boolean) => void;
  dispose: () => void;
};

export const createAutosave = ({
  isDirty,
  save,
  isFatal
}: AutosaveOptions): Autosave => {
  let active = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // When the oldest unsaved edit landed, for the continuous-typing cap.
  let burstStart: number | null = null;
  let saving = false;
  let failures = 0;

  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const schedule = (delay: number) => {
    clear();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    timer = setTimeout(() => void run(), delay);
  };

  const edited = () => {
    if (!active) return;
    if (burstStart === null) burstStart = Date.now();
    // In flight: the settle step picks this edit up. Backing off: keep waiting.
    if (saving || (failures > 0 && timer !== null)) return;
    const untilCap = burstStart + AUTOSAVE_MAX_MS - Date.now();
    schedule(Math.max(0, Math.min(AUTOSAVE_IDLE_MS, untilCap)));
  };

  const run = async () => {
    timer = null;
    if (!active || saving) return;
    if (!isDirty()) {
      burstStart = null;
      return;
    }
    saving = true;
    burstStart = null;
    try {
      await save();
      failures = 0;
    } catch (error) {
      saving = false;
      if (isFatal?.(error) || !active) return;
      const delay =
        AUTOSAVE_RETRY_MS[Math.min(failures, AUTOSAVE_RETRY_MS.length - 1)];
      failures += 1;
      schedule(delay);
      return;
    }
    saving = false;
    // An edit raced the save; it needs one of its own.
    if (active && isDirty()) edited();
  };

  return {
    edited,
    flush: () => {
      if (!active || saving || !isDirty()) return;
      clear();
      void run();
    },
    setActive: (next) => {
      if (next === active) return;
      active = next;
      if (!next) {
        clear();
        burstStart = null;
        return;
      }
      failures = 0;
      if (isDirty()) edited();
    },
    dispose: () => {
      active = false;
      clear();
    }
  };
};

/**
 * Autosave for the lifetime of a component. Call `edited()` on every change;
 * `enabled` is the host's choice, `paused` the editor's (a notice is waiting
 * on the attorney). Hiding the tab saves at once.
 */
export const useDocxAutosave = ({
  enabled,
  paused,
  isDirty,
  save,
  isFatal
}: AutosaveOptions & { enabled: boolean; paused: boolean }): Autosave => {
  const options = useRef({ isDirty, save, isFatal });
  options.current = { isDirty, save, isFatal };
  const autosave = useMemo(
    () =>
      createAutosave({
        isDirty: () => options.current.isDirty(),
        save: () => options.current.save(),
        isFatal: (error) => options.current.isFatal?.(error) ?? false
      }),
    []
  );

  useEffect(() => {
    autosave.setActive(enabled && !paused);
  }, [autosave, enabled, paused]);
  useEffect(() => () => autosave.dispose(), [autosave]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") autosave.flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [autosave, enabled]);

  return autosave;
};
